import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import type { RootState } from "../../../store/store";
import { clearAuth } from "../../../store/authSlice";
import { persistor } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";

import DenseForest from "../../../img/dense-forest.jpg";

import { getStoresPaginatedApi, StorePublic } from "../../../views/store/storeApi";
import {
  StoreRegistrationItem,
  ListStoreRegistrationsQuery,
  listStoreRegistrationsApi,
} from "../../../views/scheduling/schedulingApi";

import "./UpdatePresenceAndValuesTablePage.css";

import UpdatePresenceAndValuesTableModal from "../../../components/modal/update-presence-and-values/UpdatePresenceAndValuesTableModal";
import SubstituteFreelancerModal from "../../../components/modal/substitute-freelancer/SubstituteFreelancerModal";

const STORES_LIMIT = 10;
const ALL_STORES_ID = "__ALL__";


type StoreRegistrationRow = StoreRegistrationItem & { storeId: string; storeName: string };

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(value: string) {
  const iso = (value ?? "").slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
}

function formatTimeFromIso(iso: string): string {
  if (!iso) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/Sao_Paulo",
  }).format(new Date(iso));
}

function formatBRL(v: string | null | undefined) {
  if (!v) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
}

function safeStatusLabel(s: string) {
  return (s ?? "").toUpperCase();
}

function getRegistrationFinalValue(reg: StoreRegistrationRow) {
  const status = safeStatusLabel((reg as any)?.status);

  if (status !== "APROVADO") return "Sem Valor Definido";

  const raw =
    (reg as any)?.finalValue ??
    (reg as any)?.valorFinal ??
    (reg as any)?.payment?.totalValue ??
    (reg as any)?.payment?.finalValue ??
    (reg as any)?.paymentFinalValue ??
    null;

  const formatted = formatBRL(raw == null ? null : String(raw));
  return formatted || "-";
}

function normalizeRangeYmd(startYmd: string, endYmd: string) {
  if (startYmd <= endYmd) {
    return { startDay: startYmd, endDay: endYmd };
  }

  return { startDay: endYmd, endDay: startYmd };
}

const UpdatePresenceAndValuesTablePage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((s: RootState) => s.auth.accessToken);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");

  const selectedStore = useMemo(
    () => (selectedStoreId === ALL_STORES_ID ? null : stores.find((s) => s.id === selectedStoreId) ?? null),
    [stores, selectedStoreId],
  );

  const fetchAllStores = useCallback(async () => {
    if (!token) return;

    try {
      setLoadingStores(true);

      const all: StorePublic[] = [];
      let page = 1;

      while (true) {
        const data = await getStoresPaginatedApi(token, { page, limit: STORES_LIMIT });
        const items = data?.items ?? [];
        const total = data?.total ?? 0;

        all.push(...items);

        const doneByTotal = total > 0 && all.length >= total;
        const doneByEmpty = items.length === 0;
        if (doneByTotal || doneByEmpty) break;

        page += 1;
        if (page > 200) break;
      }

      setStores(all);
      if (!selectedStoreId && all.length > 0) setSelectedStoreId(ALL_STORES_ID);
    } catch {
      toast.error("Erro ao buscar lojas.");
    } finally {
      setLoadingStores(false);
    }
  }, [token, selectedStoreId]);

  const todayYmd = useMemo(() => toYmd(new Date()), []);
  const [selectedStartDayYmd, setSelectedStartDayYmd] = useState<string>(todayYmd);
  const [selectedEndDayYmd, setSelectedEndDayYmd] = useState<string>(todayYmd);

  const selectedRange = useMemo(
    () => normalizeRangeYmd(selectedStartDayYmd, selectedEndDayYmd),
    [selectedStartDayYmd, selectedEndDayYmd],
  );

  const [loadingRegs, setLoadingRegs] = useState(false);
  const [registrations, setRegistrations] = useState<StoreRegistrationRow[]>([]);

  const fetchRegistrations = useCallback(
    async (storeId: string, startDayYmd: string, endDayYmd: string) => {
      if (!token) return;

      try {
        setLoadingRegs(true);

        const range = normalizeRangeYmd(startDayYmd, endDayYmd);

        const query: ListStoreRegistrationsQuery = {
          startDay: range.startDay,
          endDay: range.endDay,
        };

        if (storeId === ALL_STORES_ID) {
          if (stores.length === 0) {
            setRegistrations([]);
            return;
          }

          const results = await Promise.all(
            stores.map(async (s) => {
              const regs = await listStoreRegistrationsApi(s.id, token, query);
              return (regs ?? []).map((r) => ({
                ...(r as any),
                storeId: s.id,
                storeName: s.nomeFantasia,
              })) as StoreRegistrationRow[];
            }),
          );

          const merged = results.flat();
          const sorted = [...merged].sort((a, b) => {
            const byStore = (a.storeName ?? "").localeCompare(b.storeName ?? "");
            if (byStore !== 0) return byStore;

            const byDay = String((a as any).day ?? "").localeCompare(String((b as any).day ?? ""));
            if (byDay !== 0) return byDay;

            const byTime = (a.slot?.startAt ?? "").localeCompare(b.slot?.startAt ?? "");
            if (byTime !== 0) return byTime;

            return (a.freelancer?.nomeCompleto ?? "").localeCompare(b.freelancer?.nomeCompleto ?? "");
          });

          setRegistrations(sorted);
          return;
        }

        const storeName = stores.find((s) => s.id === storeId)?.nomeFantasia ?? "";
        const data = await listStoreRegistrationsApi(storeId, token, query);

        const withStore = (data ?? []).map((r) => ({ ...(r as any), storeId, storeName })) as StoreRegistrationRow[];
        const sorted = [...withStore].sort((a, b) => {
          const byDay = String((a as any).day ?? "").localeCompare(String((b as any).day ?? ""));
          if (byDay !== 0) return byDay;

          const byTime = (a.slot?.startAt ?? "").localeCompare(b.slot?.startAt ?? "");
          if (byTime !== 0) return byTime;

          return (a.freelancer?.nomeCompleto ?? "").localeCompare(b.freelancer?.nomeCompleto ?? "");
        });

        setRegistrations(sorted);
      } catch {
        toast.error("Erro ao listar inscrições da loja.");
      } finally {
        setLoadingRegs(false);
      }
    },
    [token, stores],
  );

  const refresh = useCallback(() => {
    if (!selectedStoreId) return;
    fetchRegistrations(selectedStoreId, selectedRange.startDay, selectedRange.endDay);
  }, [selectedStoreId, selectedRange, fetchRegistrations]);

  useEffect(() => {
    fetchAllStores();
  }, [fetchAllStores]);

  useEffect(() => {
    if (!selectedStoreId) return;
    fetchRegistrations(selectedStoreId, selectedRange.startDay, selectedRange.endDay);
  }, [selectedStoreId, selectedRange, fetchRegistrations]);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedReg, setSelectedReg] = useState<StoreRegistrationRow | null>(null);

  const [isSubOpen, setIsSubOpen] = useState(false);
  const [selectedSubReg, setSelectedSubReg] = useState<StoreRegistrationRow | null>(null);

  const selectedRegStore = useMemo(() => {
    const sid = selectedReg?.storeId ?? selectedSubReg?.storeId ?? "";
    if (!sid) return selectedStore;
    return stores.find((s) => s.id === sid) ?? null;
  }, [stores, selectedReg, selectedSubReg, selectedStore]);

  const openEdit = (r: StoreRegistrationRow) => {
    setIsSubOpen(false);
    setSelectedSubReg(null);
    setSelectedReg(r);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setSelectedReg(null);
  };

  const openSub = (r: StoreRegistrationRow) => {
    setIsEditOpen(false);
    setSelectedReg(null);
    setSelectedSubReg(r);
    setIsSubOpen(true);
  };

  const closeSub = () => {
    setIsSubOpen(false);
    setSelectedSubReg(null);
  };

  useEffect(() => {
    closeEdit();
    closeSub();
  }, [selectedStoreId, selectedRange.startDay, selectedRange.endDay]);

  if (!token) return null;

  return (
    <div className="homeLayout">
      <main className="homeMain">
        <section className="ecosystem">
          <img className="ecosystem__bg-img" src={DenseForest} alt="" aria-hidden="true" />
          <div className="ecosystem__content-bg" aria-hidden="true" />

          <div className="ecosystem__content">
            <header className="ecosystem__header">
              <h1 className="ecosystem__title">
                <span className="ecosystem__title-strong">Presença e </span>
                <span className="ecosystem__title-light">valores</span>
              </h1>

              <p className="ecosystem__subtitle">Selecione loja e período para editar</p>

              <div className="ecosystem__actions">
                <button className="ecosystem__actionBtn" onClick={() => navigate(OPERACOES_PAGE())}>
                  Operações
                </button>
              </div>
            </header>

            <div className="freelancer-card-table">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <p className="freelancer-subtitle" style={{ margin: 0 }}>
                  Inscrições do período
                </p>
              </div>

              <div style={{ marginTop: 16 }} />

              <div className="SelectedInfoBox">
                <div className="PresenceTopRow">
                  <div className="PVField">
                    <label className="PVLabel">Loja</label>
                    <select
                      className="PVInput PresenceStoreSelect"
                      value={selectedStoreId}
                      onChange={(e) => setSelectedStoreId(e.target.value)}
                      disabled={loadingStores}
                    >
                      {loadingStores ? (
                        <option value="">Carregando lojas...</option>
                      ) : stores.length === 0 ? (
                        <option value="">Nenhuma loja encontrada</option>
                      ) : (
                        <>
                          <option value={ALL_STORES_ID}>Todas as lojas</option>
                          {stores.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nomeFantasia}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  <div className="PVField">
                    <label className="PVLabel">Data inicial</label>
                    <input
                      className="PVInput"
                      type="date"
                      value={selectedStartDayYmd}
                      onChange={(e) => setSelectedStartDayYmd(e.target.value)}
                      disabled={!selectedStoreId}
                    />
                  </div>

                  <div className="PVField">
                    <label className="PVLabel">Data final</label>
                    <input
                      className="PVInput"
                      type="date"
                      value={selectedEndDayYmd}
                      onChange={(e) => setSelectedEndDayYmd(e.target.value)}
                      disabled={!selectedStoreId}
                    />
                  </div>

                  <button type="button" className="PagerButton" onClick={refresh} disabled={loadingRegs || !selectedStoreId}>
                    {loadingRegs ? "Buscando..." : "Buscar"}
                  </button>
                </div>

                {selectedStoreId && (
                  <div className="PresenceMeta amplesoft-regular">
                    <span>
                      <b className="amplesoft-regular">Loja:</b>{" "}
                      {selectedStoreId === ALL_STORES_ID ? "Todas as lojas" : selectedStore?.nomeFantasia ?? "-"}
                    </span>
                    <span>
                      <b className="amplesoft-regular">Período:</b> {formatBrDate(selectedRange.startDay)} até{" "}
                      {formatBrDate(selectedRange.endDay)}
                    </span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16 }} />

              {!selectedStoreId ? (
                <div className="BoxEmpty amplesoft-bold">Selecione uma loja para visualizar as inscrições.</div>
              ) : loadingRegs ? (
                <div className="BoxEmpty amplesoft-bold">Carregando inscrições...</div>
              ) : registrations.length === 0 ? (
                <div className="BoxEmpty amplesoft-bold">Nenhuma inscrição de freelancer encontrada para este período.</div>
              ) : (
                <>
                  <div className="RatingsTableWrap PresenceTableWrap">
                    <table className="RatingsTable PresenceTable">
                      <thead>
                        <tr>
                          <th className="amplesoft">Freelancer</th>
                          <th className="amplesoft">Loja</th>
                          <th className="amplesoft">Dia</th>
                          <th className="amplesoft">Horário</th>
                          <th className="amplesoft">Status</th>
                          <th className="amplesoft">Valor</th>
                          <th className="amplesoft PresenceActionsCol">Ações</th>
                        </tr>
                      </thead>

                      <tbody>
                        {registrations.map((r) => {
                          const regDay = String((r as any).day ?? "");
                          const t1 = formatTimeFromIso(r.slot?.startAt ?? "");
                          const t2 = formatTimeFromIso(r.slot?.endAt ?? "");
                          const finalValue = getRegistrationFinalValue(r);

                          return (
                            <tr key={r.id}>
                              <td className="RatingsCell">{r.freelancer?.nomeCompleto ?? "-"}</td>
                              <td className="RatingsCell">{r.storeName ?? "-"}</td>
                              <td className="RatingsCell">{regDay ? formatBrDate(regDay) : "-"}</td>
                              <td className="RatingsCell">
                                {t1}-{t2}
                              </td>
                              <td className="RatingsCell">
                                <span className="SlotBadge SlotBadgeAccent">{safeStatusLabel(r.status)}</span>
                              </td>
                              <td className="RatingsCell">{finalValue}</td>
                              <td className="RatingsCell PresenceActionsCol">
                                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                                  <button type="button" className="PagerButton" onClick={() => openEdit(r)}>
                                    Editar
                                  </button>

                                  <button type="button" className="PagerButton PagerButtonLight" onClick={() => openSub(r)}>
                                    Substituir
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="RatingsCards PresenceCards">
                    <div className="DraftList">
                      {registrations.map((r) => {
                        const regDay = String((r as any).day ?? "");
                        const t1 = formatTimeFromIso(r.slot?.startAt ?? "");
                        const t2 = formatTimeFromIso(r.slot?.endAt ?? "");
                        const finalValue = getRegistrationFinalValue(r);

                        return (
                          <div key={r.id} className="DraftItem RatingsCard PresenceCard">
                            <div className="DraftInfo">
                              <div className="DraftTitle">{r.freelancer?.nomeCompleto ?? "-"}</div>
                              <div className="DraftSub">
                                {r.storeName ?? "-"} • {regDay ? `${formatBrDate(regDay)} • ` : ""}
                                {t1}-{t2} • Status: <b>{safeStatusLabel(r.status)}</b> • Valor: <b>{finalValue}</b>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="PagerButton PresenceConfirmBtn PresenceConfirmBtnFull"
                              onClick={() => openEdit(r)}
                              style={{ marginTop: 10 }}
                            >
                              Editar
                            </button>

                            <button
                              type="button"
                              className="PagerButton PagerButtonLight PresenceConfirmBtn PresenceConfirmBtnFull"
                              onClick={() => openSub(r)}
                              style={{ marginTop: 10 }}
                            >
                              Substituir freelancer
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {isEditOpen && selectedRegStore && selectedReg && (
          <UpdatePresenceAndValuesTableModal
            isOpen={isEditOpen}
            onClose={closeEdit}
            store={selectedRegStore}
            startDayYmd={selectedRange.startDay}
            endDayYmd={selectedRange.endDay}
            registration={selectedReg}
            onSaved={refresh}
          />
        )}

        {isSubOpen && selectedRegStore && selectedSubReg && (
          <SubstituteFreelancerModal
            isOpen={isSubOpen}
            onClose={closeSub}
            storeId={selectedRegStore.id}
            registration={selectedSubReg}
            onSuccess={refresh}
          />
        )}
      </main>
    </div>
  );
};

export default UpdatePresenceAndValuesTablePage;
